import { parseISO } from 'date-fns';

import { Stack } from '@mui/material';

import { graphql, type DocumentType } from '@/gql';

import { extractConnectionProps } from '@/utils/queries/queryExtracts';

import type {
  ReactionGroup,
  ReactionGroupUser,
} from './reaction-bar/PostReaction';
import { PostReaction } from './reaction-bar/PostReaction';

const POST_REACTION_BAR_POST_FRAGMENT = graphql(`
  fragment postReactionBarPostFields on Post {
    _id
    reactionsConnection {
      edges {
        node {
          _id
          reaction
          skinTone
          userContext
          time
          user {
            ...authorData
          }
        }
      }
    }
  }
`);

interface Props {
  post: DocumentType<typeof POST_REACTION_BAR_POST_FRAGMENT>;
}

const reactionSorter = (
  { firstCreated: timeA }: ReactionGroup,
  { firstCreated: timeB }: ReactionGroup,
) => {
  return timeA < timeB ? -1 : timeA > timeB ? 1 : 0;
};

export const PostReactionBar = ({ post }: Props) => {
  const { reactionsConnection } = post;

  const reactions = extractConnectionProps(reactionsConnection);

  const reactionMap = new Map<string, ReactionGroup>();

  reactions.map((reactionData) => {
    const { reaction, skinTone, time, user } = reactionData;

    if (!(reaction && user)) {
      return;
    }

    const newReactionData = reactionMap.get(reaction) ?? {
      reaction,
      skinTones: new Array<string>(),
      firstCreated: time ? parseISO(time) : new Date(),
      users: new Array<ReactionGroupUser>(),
    };

    if (time && parseISO(time) < newReactionData.firstCreated) {
      newReactionData.firstCreated = parseISO(time);
    }

    if (skinTone && !newReactionData.skinTones.includes(skinTone)) {
      newReactionData.skinTones.push(skinTone);
    }

    newReactionData.users.push({
      ...user,
      reactionId: reactionData._id,
    });

    reactionMap.set(reaction, newReactionData);
  });

  return (
    <Stack useFlexGap direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
      {[...reactionMap.values()].sort(reactionSorter).map((reactionGroup) => {
        return (
          <PostReaction
            key={reactionGroup.reaction}
            reactionGroup={reactionGroup}
            post={post}
          />
        );
      })}
    </Stack>
  );
};
